import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { AccountBrandPanel } from "./account-brand-panel";
import { ONBOARDING_SAFE_BOTTOM, VIEWPORT_HEIGHT_CLASS } from "./viewport-height";

/**
 * ONB-062 / ACC-013 (findings F2, S15, S3) — the account shell: a single
 * centred card holding the form. Used by BOTH halves of one flow, `/signup`
 * and `/auth`.
 *
 * ── ACC-013: why this file exists at all ──────────────────────────────────
 *
 * It is `signup-split-shell.tsx`, moved and renamed. ONB-062 rebuilt
 * `/signup` on this shell and left `/auth` exactly as it was, so the two
 * halves of one flow diverged into two products — one click apart. `/auth`
 * was a `min-h-screen` flex row with a `hidden lg:flex` brand panel (i.e.
 * nothing at all below 1024px), no touch sizing, no safe-area inset and one
 * responsive breakpoint in 1,252 lines. Adopting this shell is what closes
 * that, and the shell had to stop being called `Signup*` first: a
 * `SignupSplitShell` rendered on the sign-in page is a fact the next reader
 * has to stop and disprove.
 *
 * `signup-split-shell.tsx` survives as a compatibility surface that
 * re-exports the old `SIGNUP_*` constant names — `app/(site)/join/page.tsx`,
 * `app/(site)/start/start-page-client.tsx` and `app/(site)/signup/signup-page-client.tsx`
 * import those, and `components/studio/editor-shell/touch-safe.ts` cites the
 * old path in a comment. That is the pattern `viewport-height.ts` already
 * established one directory over: lift the definition to the neutral module,
 * keep the old name pointing at it, and change the importers in their own
 * lanes rather than in this one. New code should import from HERE.
 *
 * The `SignupPage.brand.*` translation namespace is deliberately NOT part of
 * the rename: see `account-brand-panel.tsx`'s `useTranslations` call.
 *
 * Deliberately NOT built on `OnboardingCardShell`: this is a two-column
 * split, not a centred card, and the two have nothing in common below the
 * viewport-height class they now share.
 *
 * That class is the fix for the logged mobile defect where the signup card
 * sat under the browser toolbar. It landed in the signup shell first
 * (ONB-062) because `app/(site)/join/__tests__/page.spec.tsx` pinned
 * `OnboardingCardShell`'s `min-h-screen` and that spec was out of scope;
 * ONB-063 moved the shell and that assertion together, so both shells now
 * read the SAME constant from `viewport-height.ts` rather than each keeping
 * a copy of the string.
 *
 * ── Responsive contract (four bands, brand panel never simply hidden) ─────
 *
 *  1280+  Two columns. The brand panel is a real half of the screen, and
 *         each column scrolls its own overflow rather than the page.
 *   768   One column: brand panel stacked ABOVE the form (it is what tells
 *         a stranger what this is, so it leads), form capped at 420px and
 *         centred.
 *   390   Same stack, but the brand panel has shed the four surface lines —
 *         the headline (value line) and the posture badges remain, the
 *         badges as a compact row.
 *   344   The brand panel is one line of proof above the form; the form is
 *         single-column and full-bleed (16px gutters, no card chrome).
 *
 * Which brand part survives which band is `BRAND_PART_MIN_WIDTH` in
 * account-brand-panel.tsx, not this file — one table, one place.
 */

/**
 * Re-exported, not redeclared: `viewport-height.ts` holds the one definition
 * (and the reasoning for the `supports-[…]` feature query).
 */
export { VIEWPORT_HEIGHT_CLASS };

/**
 * Every interactive target on these pages is at least 44 CSS px tall (iOS
 * HIG's 44pt; at 1dp = 1px this clears Android's 48dp once the surrounding
 * gap is counted). The stock `Button`/`Input` in this app are `h-9` (36px),
 * so this has to be applied explicitly at every call site — exported here so
 * both pages and their tests read the same constant.
 *
 * NOT `@akili/ui-responsive`'s `TOUCH_TARGET`, which is
 * `"min-h-11 min-w-11"`. The extra `min-w-11` is for a square/icon control;
 * every control in this shell's form column is `w-full`, where it is a
 * no-op. Both satisfy `findTouchSafetyViolations` (`min-h-11` is in its
 * `SIZED_TOKENS`), so the choice is only about which single value both
 * routes are pinned to — and pinning them to two nearly-identical values is
 * precisely the drift ACC-013 exists to remove. A control here that IS
 * square should import `TOUCH_TARGET` and say so.
 */
export const ACCOUNT_TOUCH_TARGET = "min-h-11";

/**
 * The primary call to action on the account shell, in Akili's own gradient.
 *
 * The default `Button` renders near-black. That is right almost everywhere in
 * the product — a neutral primary keeps a dense UI calm — but this shell is
 * the page a stranger arrives on from the marketing site, whose one primary
 * action is an indigo-to-violet pill. Landing on a black button read as a
 * different, plainer product than the one that had just been sold.
 *
 * Same two stops as the composer's send control
 * (`composer-primary-control.tsx`'s GRADIENT) and the marketing site's
 * "Start free", so the three agree rather than each approximating the brand.
 *
 * The ring is a soft coloured glow, not a border: it gives the button
 * presence on a pale surface without adding a hard edge that would fight the
 * hairline inputs above it. It is dropped on hover-capable devices only in
 * the sense that it never animates — no transition on shadow, because a
 * glow that grows on hover reads as a toy.
 *
 * Applied to the SUBMIT control of each step, never to the OAuth buttons:
 * those are the providers' own marks and must stay visually secondary, or
 * the page argues with itself about what to press.
 */
export const ACCOUNT_PRIMARY_CTA = cn(
  "bg-[linear-gradient(135deg,#0052FF_0%,#7C22F5_100%)] text-white",
  "shadow-[0_8px_24px_-8px_rgba(82,34,245,0.55)]",
  "hover:opacity-95 hover:bg-[linear-gradient(135deg,#0052FF_0%,#7C22F5_100%)]",
  "disabled:opacity-50 disabled:shadow-none",
);

/**
 * The 44px floor for an INLINE link inside a sentence, where forcing the
 * visual box to 44px would blow the line height of a paragraph apart.
 *
 * Same technique the chat composer already uses (`HIT_SLOP_8`/`HIT_SLOP_7`
 * in @akili/ui-responsive): an invisible `::after` extends the tap target
 * without changing the layout. A `text-xs` link's line box is 16px, so
 * 14px of slop above and below reaches exactly 44.
 */
export const ACCOUNT_INLINE_HIT_SLOP =
  "relative after:absolute after:inset-x-0 after:-inset-y-3.5 after:content-['']";

/**
 * Bottom padding that clears the home indicator / Fold chin on the
 * bottom-anchored end of the form column, and behaves normally on hardware
 * with no inset (`env()` resolves to 0, so `max()` keeps the ordinary
 * padding). Same single definition `OnboardingCardShell` uses, so the
 * onboarding shells inset by the same amount rather than by two
 * nearly-equal ones.
 *
 * Also NOT `@akili/ui-responsive`'s `SAFE_BOTTOM` (0.75rem), and
 * `viewport-height.ts` states why: that one is the inset for a fixed/sticky
 * bar hugging the edge, and a page whose content ends in a button wants the
 * page's own 1.5rem gutter as its floor. Checked for ACC-013 rather than
 * assumed — the signup-prefixed constant was the right one, the prefix was
 * the only thing wrong with it.
 */
export const ACCOUNT_SAFE_BOTTOM = ONBOARDING_SAFE_BOTTOM;

/**
 * ONB-062 / ACC-013 (findings F2, S15, S3) — the account shell: the brand
 * panel beside a centred form card. Used by BOTH halves of one flow,
 * `/signup` and `/auth`.
 *
 * ── The layout, and the one thing that is different about it ─────────────
 *
 * From 1280px it is a two-column grid: the lit indigo brand panel on the
 * LEFT, the form card on the RIGHT. Below 1280px it is one column — and the
 * FORM COMES FIRST, with the brand panel as a band BELOW it.
 *
 * That order is the whole point. The panel used to stack ABOVE the form on
 * every screen under 1280px, which is what most visitors actually get, so a
 * person arriving to sign in scrolled past four feature cards and three
 * pills to reach an email field. A sign-in page has one job, and the answer
 * to "what is this product?" (ACC-013/F2) is worth keeping — just not ahead
 * of the field. Below the card it is still there for anyone who scrolls, and
 * it cannot compete with the thing they came to do.
 *
 * The DOM order is form-then-panel at every width, and the grid re-places
 * them at 1280px rather than the source being reordered. So the visual left
 * column is the second node in the DOM: deliberate, because tab order and
 * screen-reader order should reach the form first no matter what the grid
 * does with the boxes.
 *
 * ── The card ─────────────────────────────────────────────────────────────
 *
 * The form used to be drawn straight onto the page background. It now sits
 * in a real surface — `bg-card`, a hairline border, `rounded-2xl` and a soft
 * shadow — on a `bg-muted/40` field, so it reads as a deliberate object
 * rather than a form floating in space. `rounded-2xl` is the admin console's
 * own sign-in card radius, so both front doors of the product agree.
 *
 * ── Why one component for both halves ────────────────────────────────────
 *
 * `/auth` and `/signup` are one flow, and a person moves between them by
 * clicking a link in the card. Two different frames on either side of that
 * link reads as two different products. `signup-split-shell.tsx` re-exports
 * this under the older name, so both halves change together and neither can
 * drift.
 *
 * The measure, the safe-area padding and the touch constants are unchanged —
 * `max-w-[420px]` is still an arbitrary value rather than `max-w-md` for the
 * reason the width evaluator documents, and the card adds no fixed-width
 * box, so the 320/344px proofs still hold.
 */
export function AccountShell({ children }: { children: ReactNode }) {
  return (
    <main
      data-testid="account-shell"
      className={cn(
        VIEWPORT_HEIGHT_CLASS,
        "bg-muted/40",
        // One column below 1280px, two from it. The brand column is given
        // slightly more room than the form column: the form is capped at
        // 420px regardless, so the extra width goes to the panel that can
        // actually use it.
        "grid grid-cols-1 min-[1280px]:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]",
      )}
    >
      <div
        data-testid="account-form-side"
        className={cn(
          // Column 2 of row 1 from 1280px — the DOM has this FIRST, and the
          // grid puts it on the right. See the header.
          "min-[1280px]:col-start-2 min-[1280px]:row-start-1",
          "relative isolate flex flex-col items-center justify-center",
          "px-4 py-10 min-[360px]:px-6 min-[768px]:px-8",
          ACCOUNT_SAFE_BOTTOM,
        )}
      >
        {/*
          A wash aimed at the card, in the brand's own indigo. Decorative and
          inert — it is what stops the form side reading as an empty grey
          rectangle next to a lit panel.
        */}
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 -z-10",
            "bg-[radial-gradient(60%_45%_at_50%_0%,rgba(99,102,241,0.10)_0%,transparent_70%)]",
          )}
        />

        <div
          data-testid="account-form-panel"
          className={cn(
            "w-full max-w-[420px]",
            "bg-card border-border rounded-2xl border shadow-sm",
            "px-5 py-8 min-[360px]:px-7 min-[768px]:px-8",
          )}
        >
          {children}
        </div>
      </div>

      <AccountBrandPanel
        className={cn(
          "min-[1280px]:col-start-1 min-[1280px]:row-start-1",
          // Below 1280px this is the last thing on the page, so it carries
          // the home-indicator gutter there; from 1280px it is a full-height
          // column beside the form and the form side carries its own.
          ACCOUNT_SAFE_BOTTOM,
          "min-[1280px]:pb-0",
        )}
      />
    </main>
  );
}

/**
 * The former name, kept so every existing import and re-export keeps working.
 * Renaming every call site in the same change as the layout would have buried
 * the layout diff.
 */
export const AccountSplitShell = AccountShell;
